import { useState, useEffect } from "react";
import { useNavigate, Link, useSearchParams } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Check, Rocket, Sparkles, Users, Clock } from "lucide-react";
import { toast } from "sonner";
import { TRADES, DURATIONS, regionsForCountry, COUNTRIES } from "@/constants/data";
import CountiesPicker from "@/components/CountiesPicker";

export default function HireCrew() {
  const navigate = useNavigate();
  const { refresh } = useAuth();
  const [params] = useSearchParams();
  const [stats, setStats] = useState(null);
  const [busy, setBusy] = useState(false);
  const [country, setCountry] = useState("Ireland");
  const [form, setForm] = useState({
    name: "",
    company: "",
    email: "",
    password: "",
    phone: "",
    county: "Dublin",
    job_title: "",
    job_trade: "Labourer",
    job_duration: "Ongoing",
    job_town: "",
    job_day_rate: "",
    job_description: "",
    job_counties: [],
    referral_code: (params.get("ref") || "").toUpperCase(),
  });

  useEffect(() => {
    api.get("/stats/public").then((r) => setStats(r.data)).catch(() => {});
  }, []);

  const setF = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const payload = {
        ...form,
        job_day_rate: form.job_day_rate ? Number(form.job_day_rate) : null,
        job_town: form.job_town || null,
        job_description: form.job_description || null,
        company: form.company || null,
        job_counties: form.job_counties || [],
      };
      const { data } = await api.post("/auth/quick-employer-signup", payload);
      await refresh();
      const matched = data?.matched_workers || 0;
      if (matched > 0) {
        toast.success(
          `Job posted — ${matched} tradesperson${matched === 1 ? "" : "s"} just got notified about it.`,
          { duration: 6000 }
        );
      } else {
        toast.success("Job posted! Workers can see it now.");
      }
      navigate("/onboarding/matches");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Signup failed");
      setBusy(false);
    }
  };

  return (
    <div data-testid="hire-crew-page" className="min-h-screen bg-neutral-950">
      <div className="max-w-6xl mx-auto px-4 md:px-8 py-10 md:py-16 grid lg:grid-cols-[1.1fr_1fr] gap-10">
        {/* LEFT — pitch */}
        <div>
          {stats && (
            <div className="inline-flex items-center gap-3 bg-neutral-900 border border-neutral-800 px-3 py-2 mb-6">
              <span className="relative flex h-2 w-2">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-amber-500 opacity-75"></span>
                <span className="relative inline-flex rounded-full h-2 w-2 bg-amber-500"></span>
              </span>
              <span className="text-[10px] uppercase tracking-widest text-neutral-300">
                <span className="text-amber-500 font-bold">{stats.workers_available}</span> workers on the board
                <span className="mx-2 text-neutral-700">·</span>
                Available <span className="text-amber-500 font-bold">right now</span>
              </span>
            </div>
          )}
          <div className="flex items-center gap-2 mb-4">
            <div className="h-[2px] w-10 bg-amber-500" />
            <span className="text-amber-500 text-xs font-bold tracking-[0.3em] uppercase">For contractors</span>
          </div>
          <h1 className="display text-5xl md:text-7xl leading-none">
            Hire crew <span className="text-amber-500">this week.</span><br />
            No agency fees.
          </h1>
          <p className="text-neutral-300 text-lg mt-6 max-w-lg">
            Free to post. Tradespeople across Ireland &amp; UK ring you back direct. Set the day rate you want to pay.
          </p>

          <div className="mt-8 grid sm:grid-cols-2 gap-3 max-w-lg">
            {[
              ["Post free", "Your first job is free. No commission, no cut."],
              ["Direct calls", "Workers ring your phone — no middleman."],
              ["Ireland &amp; UK", "From Dublin to London to Belfast — one board."],
              ["First boost FREE", "Pin your job to the top of every search."],
            ].map(([t, d]) => (
              <div key={t} className="flex gap-3 items-start">
                <Check className="w-5 h-5 text-amber-500 mt-0.5 shrink-0" strokeWidth={3} />
                <div>
                  <div className="font-bold text-sm">{t}</div>
                  <div className="text-neutral-400 text-xs mt-0.5">{d}</div>
                </div>
              </div>
            ))}
          </div>

          {stats && (
            <div className="mt-10 grid grid-cols-3 gap-4 max-w-lg">
              <div className="border-l-2 border-amber-500 pl-3">
                <div className="text-2xl font-black text-white">{stats.workers_available}</div>
                <div className="text-[10px] uppercase tracking-widest text-neutral-500 mt-1">Workers ready</div>
              </div>
              <div className="border-l-2 border-neutral-800 pl-3">
                <div className="text-2xl font-black text-white">{stats.counties_active}</div>
                <div className="text-[10px] uppercase tracking-widest text-neutral-500 mt-1">Counties active</div>
              </div>
              <div className="border-l-2 border-neutral-800 pl-3">
                <div className="text-2xl font-black text-white">{stats.applications_week}</div>
                <div className="text-[10px] uppercase tracking-widest text-neutral-500 mt-1">Apps this week</div>
              </div>
            </div>
          )}

          <div className="mt-10 flex items-center gap-3 text-neutral-500 text-xs uppercase tracking-widest">
            <Sparkles className="w-4 h-4 text-amber-500" />
            <span>4.9 avg · 200+ contractors already hiring</span>
          </div>
        </div>

        {/* RIGHT — form */}
        <div className="bg-neutral-900 border border-neutral-800 p-6 md:p-8 h-fit sticky top-4">
          <div className="flex items-center gap-2 mb-2">
            <Rocket className="w-5 h-5 text-amber-500" />
            <span className="text-[10px] uppercase tracking-[0.3em] text-amber-500 font-bold">Live in 60 seconds</span>
          </div>
          <h2 className="display text-3xl mb-6">Post a job. Start hiring.</h2>

          <form onSubmit={submit} className="space-y-4">
            {/* Job details */}
            <div className="border-l-2 border-amber-500 pl-3 -ml-3">
              <div className="text-[10px] uppercase tracking-widest text-amber-500 font-bold mb-3">The job</div>
              <div>
                <label className="text-[10px] font-bold uppercase tracking-widest text-neutral-400 block mb-1.5">Job title</label>
                <input data-testid="qh-job-title" required minLength={3} value={form.job_title} onChange={setF("job_title")} className="w-full bg-neutral-950 border border-neutral-800 focus:border-amber-500 outline-none px-4 py-3" placeholder="e.g. Site labourer needed — 2 weeks" />
              </div>
              <div className="grid grid-cols-2 gap-3 mt-3">
                <div>
                  <label className="text-[10px] font-bold uppercase tracking-widest text-neutral-400 block mb-1.5">Trade needed</label>
                  <select data-testid="qh-trade" required value={form.job_trade} onChange={setF("job_trade")} className="w-full bg-neutral-950 border border-neutral-800 focus:border-amber-500 outline-none px-4 py-3">
                    {TRADES.map((t) => <option key={t} value={t}>{t}</option>)}
                  </select>
                </div>
                <div>
                  <label className="text-[10px] font-bold uppercase tracking-widest text-neutral-400 block mb-1.5">Duration</label>
                  <select data-testid="qh-duration" required value={form.job_duration} onChange={setF("job_duration")} className="w-full bg-neutral-950 border border-neutral-800 focus:border-amber-500 outline-none px-4 py-3">
                    {DURATIONS.map((d) => <option key={d} value={d}>{d}</option>)}
                  </select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3 mt-3">
                <div>
                  <label className="text-[10px] font-bold uppercase tracking-widest text-neutral-400 block mb-1.5">Country</label>
                  <div className="flex gap-2">
                    {COUNTRIES.map((c) => (
                      <button
                        type="button"
                        key={c}
                        data-testid={`qh-country-${c === "Ireland" ? "ie" : "uk"}`}
                        onClick={() => {
                          setCountry(c);
                          const first = regionsForCountry(c)[0];
                          setForm((f) => ({ ...f, county: first, job_counties: [] }));
                        }}
                        className={`flex-1 px-3 py-3 border text-xs uppercase tracking-widest transition-colors ${
                          country === c
                            ? "bg-amber-500 text-neutral-950 border-amber-500 font-bold"
                            : "bg-neutral-950 border-neutral-800 text-neutral-400 hover:border-amber-500"
                        }`}
                      >
                        {c === "Ireland" ? "🇮🇪 IE" : "🇬🇧 UK"}
                      </button>
                    ))}
                  </div>
                </div>
                <div>
                  <label className="text-[10px] font-bold uppercase tracking-widest text-neutral-400 block mb-1.5">{country === "Ireland" ? "County" : "Region"}</label>
                  <select data-testid="qh-county" required value={form.county} onChange={setF("county")} className="w-full bg-neutral-950 border border-neutral-800 focus:border-amber-500 outline-none px-4 py-3">
                    {regionsForCountry(country).map((c) => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
              </div>
              <div className="mt-3">
                <label className="text-[10px] font-bold uppercase tracking-widest text-neutral-400 block mb-1.5">Day rate <span className="text-neutral-600 font-normal normal-case">(optional)</span></label>
                <input data-testid="qh-rate" type="number" min="0" value={form.job_day_rate} onChange={setF("job_day_rate")} className="w-full bg-neutral-950 border border-neutral-800 focus:border-amber-500 outline-none px-4 py-3" placeholder={country === "Ireland" ? "€250" : "£200"} />
              </div>
              <div className="mt-3">
                <label className="text-[10px] font-bold uppercase tracking-widest text-neutral-400 block mb-1.5">
                  Also covers <span className="text-neutral-600 font-normal normal-case">(optional — tick any county this job also covers)</span>
                </label>
                <CountiesPicker
                  primary={form.county}
                  value={form.job_counties}
                  onChange={(next) => setForm((f) => ({ ...f, job_counties: next }))}
                />
                {form.job_counties.length > 0 && (
                  <div className="mt-2 text-[10px] uppercase tracking-widest text-amber-500">
                    +{form.job_counties.length} extra {form.job_counties.length === 1 ? "county" : "counties"} — more workers will see it
                  </div>
                )}
              </div>
            </div>

            {/* Contact details */}
            <div className="border-l-2 border-neutral-800 pl-3 -ml-3 pt-2">
              <div className="text-[10px] uppercase tracking-widest text-neutral-500 font-bold mb-3">Your details</div>
              <div>
                <label className="text-[10px] font-bold uppercase tracking-widest text-neutral-400 block mb-1.5">Your name</label>
                <input data-testid="qh-name" required value={form.name} onChange={setF("name")} className="w-full bg-neutral-950 border border-neutral-800 focus:border-amber-500 outline-none px-4 py-3" placeholder="John Murphy" />
              </div>
              <div className="mt-3">
                <label className="text-[10px] font-bold uppercase tracking-widest text-neutral-400 block mb-1.5">Company <span className="text-neutral-600 font-normal normal-case">(optional)</span></label>
                <input data-testid="qh-company" value={form.company} onChange={setF("company")} className="w-full bg-neutral-950 border border-neutral-800 focus:border-amber-500 outline-none px-4 py-3" placeholder="Murphy Construction Ltd" />
              </div>
              <div className="mt-3">
                <label className="text-[10px] font-bold uppercase tracking-widest text-neutral-400 block mb-1.5">Mobile <span className="text-neutral-600 font-normal normal-case">(so workers can ring you)</span></label>
                <input data-testid="qh-phone" required value={form.phone} onChange={setF("phone")} className="w-full bg-neutral-950 border border-neutral-800 focus:border-amber-500 outline-none px-4 py-3" placeholder="083 123 4567" />
              </div>
              <div className="mt-3">
                <label className="text-[10px] font-bold uppercase tracking-widest text-neutral-400 block mb-1.5">Email</label>
                <input data-testid="qh-email" required type="email" value={form.email} onChange={setF("email")} className="w-full bg-neutral-950 border border-neutral-800 focus:border-amber-500 outline-none px-4 py-3" placeholder="you@email.com" />
              </div>
              <div className="mt-3">
                <label className="text-[10px] font-bold uppercase tracking-widest text-neutral-400 block mb-1.5">Password (min 6)</label>
                <input data-testid="qh-password" required type="password" minLength={6} value={form.password} onChange={setF("password")} className="w-full bg-neutral-950 border border-neutral-800 focus:border-amber-500 outline-none px-4 py-3" placeholder="••••••••" />
              </div>
            </div>

            {form.referral_code && (
              <div>
                <label className="text-[10px] font-bold uppercase tracking-widest text-amber-500 block mb-1.5">Referral code applied ✓</label>
                <input data-testid="qh-ref" readOnly value={form.referral_code} className="w-full bg-neutral-950 border border-amber-500/60 text-amber-400 outline-none px-4 py-3 font-mono tracking-widest" />
              </div>
            )}

            <button
              data-testid="qh-submit"
              type="submit"
              disabled={busy}
              className="w-full py-4 bg-amber-500 text-neutral-950 uppercase tracking-widest text-sm font-bold hover:bg-amber-400 transition-colors disabled:opacity-60 inline-flex items-center justify-center gap-2"
            >
              <Rocket className="w-4 h-4" />
              {busy ? "Posting your job…" : "Post job — free"}
            </button>
            <div className="text-center text-[10px] uppercase tracking-widest text-neutral-500">
              No card required · Cancel anytime · <Link to="/login" className="text-amber-500 hover:underline">Already have an account?</Link>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
